import {
  useApproversByFunctionId,
  EventType,
  EquipmentIdType,
  Priority,
  BaseEvent,
  Schedule,
  ShiftEvent,
  SystemChangeRequestData,
  SystemChangeRequest,
  useRequiredConfig
} from '@domain/shifthandover'
import {EventDescriptionBox} from '@widgets/common-shift-event'
import {
  CategoryDropDown,
  PriorityDropDown,
  EventProcessStageAutocomplete,
  ProcessStageDropDown
} from '@hconnect/common/components/shiftEventFormFields'
import {EventHighlight} from '@hconnect/common/components/shiftEventFormFields/EventHighlight'
import {REGULAR_SIZE, INPUT_FORM_FIELD_SX} from '@hconnect/common/consts'
import {useRequiredUser} from '@hconnect/login'
import {Grid, TextField, Box} from '@mui/material'
import {uniqBy} from 'lodash'
import React, {useMemo, useEffect} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {getParameterChangeStates} from '../../utils'
import {ApproversAdjustButton} from '../dialogs/approversAdjust/ApproversAdjustButton'
import {EquipmentSearchContainer} from '../eventProperties'
import {ApproversField} from '../eventProperties/ApproversField'
import {EventInfo} from '../eventProperties/EventInfo'
import {EventTypeDropdownBox} from '../eventProperties/EventTypeDropdownBox'
import {ParameterChangeFunctionDropdown} from '../eventProperties/ParameterChangeFunctionDropdown'
import {ParameterChangeStatusDropDown} from '../eventProperties/ParameterChangeStatusDropDown'
import {ScheduleSwitch} from '../eventProperties/ScheduleSwitch'

type TypeOfSystemChangeRequest = SystemChangeRequest

type PossibleErrors =
  | keyof TypeOfSystemChangeRequest
  | 'parameterChange.functionId'
  | 'equipmentNumber'
  | 'parameterChange'
  | 'approvers'
  | 'parameterChange.currentValue'
  | 'parameterChange.newValue'

type SystemChangeRequestFormProps = {
  plantId: string
  item: Partial<TypeOfSystemChangeRequest>
  update: <K extends keyof TypeOfSystemChangeRequest>(
    key: K,
    value: TypeOfSystemChangeRequest[K] | EventType
  ) => void
  updateMultiPart: (delta) => void
  validationError: Map<PossibleErrors, string>
  equipmentIdType?: EquipmentIdType
  isEditMode: boolean
  children: React.ReactNode
  autocompleteProcessStage?: boolean
}

export const systemChangeRequestDefaultObject = (): Partial<ShiftEvent> => ({
  description: '',
  eventType: 'systemChangeRequest',
  status: 'waitingForApproval',
  title: '',
  parameterChange: {
    currentValue: '',
    newValue: '',
    functionId: ''
  },
  approvers: [],
  needsApproval: true
})

export const SystemChangeRequestForm: React.FC<SystemChangeRequestFormProps> = ({
  plantId,
  children,
  item,
  update,
  updateMultiPart,
  validationError,
  isEditMode,
  equipmentIdType,
  autocompleteProcessStage
}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const config = useRequiredConfig(plantId)
  const user = useRequiredUser()

  const {data: requiredApprovers, isSuccess: isApproversSuccess} = useApproversByFunctionId({
    plantId,
    functionId: item.parameterChange?.functionId,
    options: {
      enabled: item.needsApproval && !!item.parameterChange?.functionId
    }
  })

  useEffect(() => {
    if (isApproversSuccess && requiredApprovers.length > 0) {
      const currentApprovers = item.approvers || []
      const manuallyAddedApprovers = currentApprovers.filter(
        (approver) => !requiredApprovers.some((auto) => auto.user.id === approver.user.id)
      )

      const updatedApprovers = uniqBy([...requiredApprovers, ...manuallyAddedApprovers], 'user.id')

      updateMultiPart({approvers: updatedApprovers})
    }
    // excluding item.approvers to avoid infinite loop since item.approvers is updated here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApproversSuccess, requiredApprovers, updateMultiPart])

  const getErrorText = (key: PossibleErrors) => {
    const errorKey = validationError.get(key)
    return errorKey && t(errorKey)
  }

  const setParameterChangeData = (value: Partial<SystemChangeRequestData>) => {
    updateMultiPart({
      parameterChange: {
        ...item.parameterChange,
        ...value
      }
    })
  }

  const {disableField, disableNeedsApproval, disableApproversField} = useMemo(
    () =>
      getParameterChangeStates(
        isEditMode,
        item.needsApproval,
        (item as ShiftEvent)?.createdBy?.id,
        user.id,
        item.approvers,
        item.status === 'waitingForApproval'
      ),
    [item, user.id, isEditMode]
  )

  return (
    <>
      <Box display="flex" gap={2} mb={2}>
        <TextField
          data-test-id="event-form-title"
          error={validationError.has('title')}
          fullWidth={true}
          helperText={getErrorText('title')}
          label={t('shiftEvent.label.title')}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            update('title', event.target.value)
          }
          required
          value={item.title}
          variant="filled"
          InputProps={INPUT_FORM_FIELD_SX}
          disabled={disableField}
        />

        <EventHighlight
          title={t('eventsTable.action.highlight')}
          value={item.isHighlighted}
          onChange={(value) => update('isHighlighted', value)}
          disabled={disableField}
        />
      </Box>

      {'created' in item && (
        <EventInfo
          item={item as BaseEvent}
          workOrderTarget={config.workOrderTarget}
          plantId={plantId}
        />
      )}

      <Box my={1}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item {...REGULAR_SIZE}>
            <EventTypeDropdownBox
              item={item as BaseEvent}
              value="systemChangeRequest"
              updateMultiPart={updateMultiPart}
            />
          </Grid>
          <Grid item {...REGULAR_SIZE}>
            <ParameterChangeStatusDropDown
              translate={t}
              eventData={item as ShiftEvent}
              data-test-id="event-form-status"
              errorText={getErrorText('status')}
              onChange={(value) => update('status', value)}
              disabled={disableField}
            />
          </Grid>

          <Grid item xs={12}>
            <EquipmentSearchContainer
              plantId={plantId}
              data-test-id="event-form-equipmentNumber"
              errorText={getErrorText('equipmentNumber')}
              updateMultiPart={updateMultiPart}
              value={item as BaseEvent}
              disabled={disableField}
              equipmentIdType={equipmentIdType}
            />
          </Grid>

          {autocompleteProcessStage ? (
            <Grid item xs={12}>
              <EventProcessStageAutocomplete
                plantId={plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value?: string) => update('processStage', value)}
                value={item.processStage}
              />
            </Grid>
          ) : (
            <Grid item {...REGULAR_SIZE}>
              <ProcessStageDropDown
                translate={t}
                plantId={config.plantId}
                data-test-id="event-form-processStage"
                errorText={getErrorText('processStage')}
                onChange={(value) => update('processStage', value)}
                value={item.processStage}
                disabled={disableField}
              />
            </Grid>
          )}
          <Grid item {...REGULAR_SIZE}>
            <CategoryDropDown
              translate={t}
              data-test-id="event-form-category"
              onChange={(value) => update('category', value)}
              value={item.category}
              disabled={disableField}
            />
          </Grid>

          <Grid item {...REGULAR_SIZE}>
            <PriorityDropDown
              translate={t}
              data-test-id="event-form-priority"
              errorText={getErrorText('priority')}
              onChange={(value) => update('priority', value)}
              value={item.priority as Priority}
              disabled={disableField}
            />
          </Grid>

          <Grid item {...REGULAR_SIZE} display="flex" gap={1.5}>
            <ParameterChangeFunctionDropdown
              translate={t}
              data-test-id="event-form-functionId"
              errorText={getErrorText('parameterChange.functionId')}
              onChange={(value) => {
                setParameterChangeData({
                  functionId: value
                })
              }}
              required
              value={item.parameterChange?.functionId}
              disabled={disableField}
            />
            <ApproversAdjustButton plantId={plantId} />
          </Grid>

          <ScheduleSwitch
            label={t('shiftEvent.label.temporary')}
            disabled={disableField}
            item={item}
            onChange={(value: Schedule | undefined) => {
              updateMultiPart({
                schedule: value,
                status: item.needsApproval ? 'waitingForApproval' : 'pending'
              })
            }}
            errorText={getErrorText('schedule')}
            plantId={plantId}
          />

          <Grid item xs={12}>
            <TextField
              data-test-id="event-form-parameter-current"
              error={validationError.has('parameterChange.currentValue')}
              fullWidth={true}
              helperText={getErrorText('parameterChange.currentValue')}
              label={t('shiftEvent.label.currentValue')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setParameterChangeData({currentValue: event.target.value})
              }}
              value={item.parameterChange?.currentValue}
              variant="filled"
              InputProps={INPUT_FORM_FIELD_SX}
              disabled={disableField}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              data-test-id="event-form-parameter-new"
              error={validationError.has('parameterChange.newValue')}
              fullWidth={true}
              helperText={getErrorText('parameterChange.newValue')}
              label={t('shiftEvent.label.newValue')}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setParameterChangeData({newValue: event.target.value})
              }
              value={item.parameterChange?.newValue}
              variant="filled"
              InputProps={INPUT_FORM_FIELD_SX}
              disabled={disableField}
            />
          </Grid>

          <ApproversField
            plantId={plantId}
            toggleApprovers={(needsApproval) => {
              const functionId = item.parameterChange?.functionId
              const defaultApprovers = functionId && config.plantId ? requiredApprovers || [] : []
              updateMultiPart({
                needsApproval,
                approvers: needsApproval ? defaultApprovers : undefined
              })
              update('status', needsApproval ? 'waitingForApproval' : 'pending')
            }}
            onChange={(approvers) => {
              updateMultiPart({approvers})
            }}
            data-test-id="event-form-approverStage"
            errorText={getErrorText('approvers')}
            approvers={item.approvers || []}
            needsApproval={item.needsApproval}
            disableNeedsApproval={disableNeedsApproval}
            disableApproversField={disableApproversField}
            requiredApprovers={requiredApprovers?.map(({user}) => user)}
          />

          <Grid item xs={12}>
            <EventDescriptionBox
              current={item.description || ''}
              errorCode={validationError.get('description')}
              onChange={(next) => update('description', next)}
              required
              disabled={disableField}
            />
          </Grid>
        </Grid>
      </Box>
      {children}
    </>
  )
}
